import React from "react";
import { ReactComponent as CloudIcon } from "@assets/Icons/IconConnectivityKontrol.svg";
import { resolveCloudProviderLogo } from "kontrolEdge/helpers/cloudProviderLogo";
import { ServiceHealth } from "./overviewModel";

/**
 * What the map and the site diagram draw the same way: a site's border, a
 * line's colour by health, and the legend. One place, so the two views of the
 * same topology cannot drift apart.
 */

export const PILL_BORDER: Record<ServiceHealth, string> = {
  up: "border-semantic-border-primary",
  unknown: "border-semantic-border-primary",
  warning: "border-semantic-fg-warning",
  down: "border-semantic-fg-danger",
};

export const HEALTH_STROKE: Record<ServiceHealth, string> = {
  up: "stroke-semantic-bg-brand-bold",
  warning: "stroke-semantic-fg-warning",
  down: "stroke-semantic-fg-danger",
  unknown: "stroke-semantic-fg-secondary",
};

export const HEALTH_FILL: Record<ServiceHealth, string> = {
  up: "fill-semantic-bg-brand-bold",
  warning: "fill-semantic-fg-warning",
  down: "fill-semantic-fg-danger",
  unknown: "fill-semantic-fg-secondary",
};

export function TopologyLegend({ hasLinks = true, hasClouds }: { hasLinks?: boolean; hasClouds: boolean }) {
  return (
    <div className="absolute bottom-3 left-3 flex items-center gap-3 rounded-md border border-semantic-border-primary bg-semantic-bg-primary px-3 py-1.5 text-[11px] text-semantic-text-secondary">
      {hasLinks && (
        <span className="flex items-center gap-1.5">
          <span className="h-0.5 w-4 rounded bg-semantic-bg-brand-bold" aria-hidden /> DCI
        </span>
      )}
      {hasClouds && (
        <span className="flex items-center gap-1.5">
          <span className="w-4 border-t-2 border-dashed border-semantic-bg-brand-bold" aria-hidden /> Cloud
        </span>
      )}
      <span className="flex items-center gap-1.5">
        <span className="h-2 w-2 rounded-full bg-semantic-fg-warning" aria-hidden /> Attention
      </span>
    </div>
  );
}

/**
 * The disc behind a cloud's mark. White under a vendor logo in both themes:
 * the logos are drawn for a light ground (AWS's wordmark is near-black).
 */
export function cloudPlateClass(cloud: string): string {
  return resolveCloudProviderLogo(cloud) ? "fill-white" : "fill-semantic-bg-primary";
}

/** A cloud node's mark: the vendor's logo when we hold one, the generic cloud otherwise. */
export function CloudMark({
  cloud,
  className,
  style,
}: {
  cloud: string;
  /** Placement and entrance classes; the size comes from here. */
  className: string;
  style: React.CSSProperties;
}) {
  const logo = resolveCloudProviderLogo(cloud);
  if (logo) {
    return <img src={logo} alt="" aria-hidden className={`h-5 w-5 object-contain ${className}`} style={style} />;
  }
  return <CloudIcon className={`h-4 w-4 text-semantic-text-brand ${className}`} style={style} aria-hidden />;
}
